/**
 * The four flags, drawn rather than fetched. SPEC.md §19, §17.1.
 *
 * Not emoji. `🇬🇧` is two regional-indicator letters that the platform is free to
 * render as a flag or as the literal letters "GB", and Windows Chrome — which is
 * a real share of the desktop traffic this app sees — does the second. A picker
 * whose orbs read "GB CN VN TH" on some devices and show flags on others is a
 * picker that has to be described in words as well, which defeats the point of
 * an orb.
 *
 * Not an image either. Four flags at three densities is twelve requests and
 * twelve things to cache for something a browser draws from twenty lines of
 * geometry, and an SVG stays sharp at any orb size without a `srcset`.
 *
 * They are `aria-hidden` everywhere they appear: a flag is not a language, and
 * the button around one carries the language's own name for the accessible name.
 * The flag is what makes the choice findable at a glance; the endonym under it
 * is what makes it correct. §19
 */

// From `locales.ts` rather than from the barrel, and deliberately: the barrel
// pulls in all four catalogues, and this component is drawn on the landing
// screen. See the note at the top of `locales.ts`. §17.1
import type { FlagCode } from './locales';

/** A five-pointed star, outer radius 1, point up. Scaled and placed per flag. */
const STAR =
  'M0,-1 L0.2245,-0.309 L0.9511,-0.309 L0.3633,0.118 L0.5878,0.809 ' +
  'L0,0.382 L-0.5878,0.809 L-0.3633,0.118 L-0.9511,-0.309 L-0.2245,-0.309 Z';

function Star({ x, y, r, fill }: { x: number; y: number; r: number; fill: string }) {
  return <path d={STAR} fill={fill} transform={`translate(${x} ${y}) scale(${r})`} />;
}

/**
 * The Union Flag. §19
 *
 * The red saltire is counterchanged — offset within each quarter rather than
 * centred on the white one — which is the detail that separates the real flag
 * from the thing people draw from memory. The clip path is four triangles
 * radiating from the centre, each selecting the half of its quarter the red arm
 * belongs in.
 *
 * Drawn 3:2 rather than the official 1:2, because it is cropped to a circle:
 * at 1:2 the crop would take the ends off the horizontal arm of the cross and
 * leave a flag that is mostly blue field.
 */
function UnionFlag() {
  return (
    <>
      <rect width="60" height="40" fill="#012169" />
      <path d="M0,0 L60,40 M60,0 L0,40" stroke="#FFFFFF" strokeWidth="8" />
      <clipPath id="tutorial-flag-gb-counterchange">
        <path d="M30,20 L60,0 v20 z M30,20 L60,40 h-30 z M30,20 L0,40 v-20 z M30,20 L0,0 h30 z" />
      </clipPath>
      <path
        d="M0,0 L60,40 M60,0 L0,40"
        stroke="#C8102E"
        strokeWidth="4"
        clipPath="url(#tutorial-flag-gb-counterchange)"
      />
      <path d="M30,0 V40 M0,20 H60" stroke="#FFFFFF" strokeWidth="13" />
      <path d="M30,0 V40 M0,20 H60" stroke="#C8102E" strokeWidth="8" />
    </>
  );
}

/** The five stars of the PRC flag, the four small ones circling the large. */
function ChinaFlag() {
  return (
    <>
      <rect width="60" height="40" fill="#EE1C25" />
      <Star x={10} y={10} r={6.4} fill="#FFDE00" />
      <Star x={20.5} y={3.4} r={2.1} fill="#FFDE00" />
      <Star x={24.6} y={8} r={2.1} fill="#FFDE00" />
      <Star x={24.6} y={14} r={2.1} fill="#FFDE00" />
      <Star x={20.5} y={18.6} r={2.1} fill="#FFDE00" />
    </>
  );
}

function VietnamFlag() {
  return (
    <>
      <rect width="60" height="40" fill="#DA251D" />
      <Star x={30} y={20} r={12} fill="#FFFF00" />
    </>
  );
}

/** The Thai trairanga: five bands, the centre one double height. */
function ThailandFlag() {
  return (
    <>
      <rect width="60" height="40" fill="#A51931" />
      <rect y="6.667" width="60" height="26.667" fill="#F4F5F8" />
      <rect y="13.333" width="60" height="13.333" fill="#2D2A4A" />
    </>
  );
}

const FLAGS: Record<FlagCode, () => React.JSX.Element> = {
  gb: UnionFlag,
  cn: ChinaFlag,
  vn: VietnamFlag,
  th: ThailandFlag,
};

export interface FlagProps {
  code: FlagCode;
  className?: string;
}

/**
 * One flag, filling whatever box it is given. §19
 *
 * `slice` rather than `meet`, because the box it is given is a circle: `meet`
 * would letterbox a 3:2 flag inside a round window and leave two crescents of
 * background, which reads as a broken image rather than as a flag.
 */
export function Flag({ code, className }: FlagProps) {
  const Drawing = FLAGS[code];
  return (
    <svg
      viewBox="0 0 60 40"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <Drawing />
    </svg>
  );
}
